import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import {
	LocalizeNameSpaceTypes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import { getKeys } from '@gov-nx/core/types';
import type { Tree } from '@gov-nx/ui/types';
import { dataBoxParams, useDataBoxShape } from '@gov-nx/utils/common';
import type { DataRequest, FormData, FormDataBody } from './service.types';

const defaultValues = (): { [key in keyof FormDataBody]: boolean } => ({
	vsechnyUdaje: true,
	_referential: true,
	_nonReferential: true,
	_operational: true,
	zaznamPoskytnutiUdaju: true,
	datumPosledniZmenyUdaju: true,
	certifikat: true,
	email: true,
	telefon: true,
	zaznamDs: true,
	dorucovaciAdresa: true,
	statniObcanstvi: true,
	omezeniSvepravnosti: true,
	mistoNarozeni: true,
	prijmeni: true,
	rodnePrijmeni: true,
	jmeno: true,
	pohlavi: true,
	rodinnyStavPartnerstvi: true,
	adresa: true,
	datumNarozeni: true,
	cislaEId: true,
});

export interface FormInstanceProps {
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	isLowAuthorization: boolean;
}

export function FormInstance({
	dataBoxTypes,
	isLowAuthorization,
}: FormInstanceProps): FormDefinition<FormData> {
	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization,
	});

	const fieldsSchema = getKeys(defaultValues()).reduce(
		(all, key) => ({ ...all, [key]: yup.boolean().required() }),
		{} as { [key in keyof FormDataBody]: yup.BooleanSchema<boolean> }
	);
	const formSchema: yup.ObjectSchema<FormData> = yup
		.object({
			...getDataBoxShape({ isRequired: true }),
			...fieldsSchema,
			_referential: yup.boolean().required(),
			_nonReferential: yup.boolean().required(),
			_operational: yup.boolean().required(),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			...getDataBoxDefaultValues(),
			...defaultValues(),
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (formData: FormData): DataRequest => {
	const isAll = formData.vsechnyUdaje;

	return {
		params: dataBoxParams(formData),
		body: {
			typVypisu: 'UDAJE_ROB',
			data: {
				...(isAll
					? { vsechnyUdaje: true }
					: {
							...getKeys(defaultValues()).reduce(
								(all, key) =>
									formData[key] ? { ...all, [key]: formData[key] } : all,
								{} as FormDataBody
							),
						}),
			},
		},
	};
};

export const useTree = (): Tree => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(LocalizeNameSpaceTypes.Form);
	return {
		name: 'vsechnyUdaje',
		label: tsn('vyber-udaju.stitky.vsechny-udaje'),
		isOpen: true,
		isToggleable: false,
		children: [
			{
				name: '_referential',
				label: tsn('vyber-udaju.stitky.referencni-udaje'),
				isOpen: false,
				isToggleable: true,
				children: [
					{ name: 'prijmeni', label: tsn('vyber-udaju.stitky.prijmeni') },
					{
						name: 'rodnePrijmeni',
						label: tsn('vyber-udaju.stitky.rodne-prijmeni'),
					},
					{ name: 'jmeno', label: tsn('vyber-udaju.stitky.jmeno') },
					{ name: 'pohlavi', label: tsn('vyber-udaju.stitky.pohlavi') },
					{
						name: 'rodinnyStavPartnerstvi',
						label: tsn('vyber-udaju.stitky.rodinny-stav-partnerstvi'),
					},
					{ name: 'adresa', label: tsn('vyber-udaju.stitky.adresa') },
					{
						name: 'datumNarozeni',
						label: tsn('vyber-udaju.stitky.datum-narozeni'),
					},
					{
						name: 'mistoNarozeni',
						label: tsn('vyber-udaju.stitky.misto-narozeni'),
					},
					{
						name: 'omezeniSvepravnosti',
						label: tsn('vyber-udaju.stitky.omezeni-svepravnosti'),
					},
					{
						name: 'statniObcanstvi',
						label: tsn('vyber-udaju.stitky.statni-obcanstvi'),
					},
					{ name: 'cislaEId', label: tsn('vyber-udaju.stitky.cisla-eid') },
					{ name: 'zaznamDs', label: tsn('vyber-udaju.stitky.zaznam-ds') },
				],
			},
			{
				name: '_nonReferential',
				label: tsn('vyber-udaju.stitky.nereferencni-udaje'),
				isOpen: false,
				isToggleable: true,
				children: [
					{ name: 'telefon', label: tsn('vyber-udaju.stitky.telefon') },
					{ name: 'email', label: tsn('vyber-udaju.stitky.email') },
					{ name: 'certifikat', label: tsn('vyber-udaju.stitky.certifikat') },
				],
			},
			{
				name: '_operational',
				label: tsn('vyber-udaju.stitky.provozni-udaje'),
				isOpen: false,
				isToggleable: true,
				children: [
					{
						name: 'zaznamPoskytnutiUdaju',
						label: tsn('vyber-udaju.stitky.zaznam-poskytnuti-udaju'),
					},
					{
						name: 'datumPosledniZmenyUdaju',
						label: tsn('vyber-udaju.stitky.datum-posledni-zmeny-udaju'),
					},
				],
			},
		],
	};
};
